import { Routes, Route } from "react-router-dom";
import { SignInPage } from "./pages/sign-in.js";
import { VerifyPage } from "./pages/verify.js";
import { OrgSetupPage } from "./pages/org-setup.js";
import { AddWebsitePage } from "./pages/add-website.js";
import { WizardRouter } from "./pages/wizard/index.js";
import { RootRedirect } from "./pages/root-redirect.js";
import { ProtectedRoute } from "./pages/protected-route.js";
import { WebsiteShell } from "./components/website-shell.js";
import { DashboardPage } from "./pages/dashboard.js";
import { PipelinePage } from "./pages/pipeline.js";
import { ReportPage } from "./pages/report.js";
import { StatementPage } from "./pages/statement.js";
import { DocumentsPage } from "./pages/documents.js";
import { RecurringPage } from "./pages/recurring.js";
import { TemplatesPage } from "./pages/templates.js";
import { WebsitesPage } from "./pages/websites.js";
import { BillingSuccessPage } from "./pages/billing-success.js";
import { BillingPage } from "./pages/billing.js";
import { SettingsPage } from "./pages/settings.js";
import { PublicStatementPage } from "./pages/public-statement.js";
import { HelpPage } from "./pages/help.js";
import { NotFoundPage } from "./pages/not-found.js";
import { AdminLayout } from "./pages/admin/index.js";
import { AdminStatsPage } from "./pages/admin/stats-page.js";
import { AdminUsersPage } from "./pages/admin/users-page.js";
import { AdminUserDetailPage } from "./pages/admin/user-detail-page.js";
import { AdminBillingEventsPage } from "./pages/admin/billing-events-page.js";
import { AdminOrgsPage } from "./pages/admin/orgs-page.js";
import { AdminOrgDetailPage } from "./pages/admin/org-detail-page.js";
import { AdminWebsitesPage } from "./pages/admin/websites-page.js";
import { AdminWebsiteDetailPage } from "./pages/admin/website-detail-page.js";
import { AdminRunsPage } from "./pages/admin/runs-page.js";
import { AdminDocumentsPage } from "./pages/admin/documents-page.js";
import { AdminStatementsPage } from "./pages/admin/statements-page.js";
import { AdminRecurringPage } from "./pages/admin/recurring-page.js";
import { AdminSupportPage } from "./pages/admin/support-page.js";
import { AdminStoragePage } from "./pages/admin/storage-page.js";
import { ErrorLogsPage } from "./pages/admin/error-logs-page.js";
import { AdminPlansPage } from "./pages/admin/plans-page.js";
import { AdminPlanDetailPage } from "./pages/admin/plan-detail-page.js";
import { AdminPipelinePage } from "./pages/admin/pipeline-page.js";
import { AdminAuditLogPage } from "./pages/admin/audit-log-page.js";
import { ErrorBoundary } from "./components/error-boundary.js";
import { CookieConsent } from "./components/cookie-consent.js";
import { PublicHomePage } from "./pages/public/home.js";
import { HowItWorksPage } from "./pages/public/how-it-works.js";
import { PlansPage } from "./pages/public/plans.js";
import { MethodologyPage } from "./pages/public/methodology.js";
import { StatementGeneratorPage } from "./pages/public/statement-generator.js";
import { DocumentAccessibilityPage } from "./pages/public/document-accessibility.js";
import { RecurringMonitoringPage } from "./pages/public/recurring-monitoring.js";
import { ManualReviewPage } from "./pages/public/manual-review.js";
import { SupportPage } from "./pages/public/support.js";
import { ContactPage } from "./pages/public/contact.js";
import { PrivacyPage as PublicPrivacyPage } from "./pages/public/privacy-public.js";
import { TermsPage as PublicTermsPage } from "./pages/public/terms-public.js";
import CertificateVerificationPage from "./pages/cert/[token].js";

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public marketing site */}
        <Route path="/" element={<PublicHomePage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        <Route
          path="/statement-generator"
          element={<StatementGeneratorPage />}
        />
        <Route
          path="/document-accessibility"
          element={<DocumentAccessibilityPage />}
        />
        <Route
          path="/recurring-monitoring"
          element={<RecurringMonitoringPage />}
        />
        <Route path="/manual-review" element={<ManualReviewPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Auth routes */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/verify" element={<VerifyPage />} />

        {/* Legal routes (public) */}
        <Route path="/privacy" element={<PublicPrivacyPage />} />
        <Route path="/terms" element={<PublicTermsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/statement/:websiteId" element={<PublicStatementPage />} />
        <Route path="/cert/:token" element={<CertificateVerificationPage />} />

        {/* Protected app routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/app" element={<RootRedirect />} />
          <Route path="/setup/org" element={<OrgSetupPage />} />
          <Route path="/setup/website" element={<AddWebsitePage />} />
          <Route path="/websites/add" element={<AddWebsitePage />} />
          <Route
            path="/setup/billing-success"
            element={<BillingSuccessPage />}
          />
          <Route path="/wizard/:websiteId/*" element={<WizardRouter />} />
          <Route path="/websites" element={<WebsitesPage />} />
          <Route path="/billing" element={<BillingPage />} />

          {/* Product routes — website-scoped */}
          <Route path="/websites/:websiteId" element={<WebsiteShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="scan" element={<PipelinePage />} />
            {/* Redirect old /pipeline URL for bookmarks */}
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="report" element={<ReportPage />} />
            <Route path="statement" element={<StatementPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="recurring" element={<RecurringPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Admin routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminStatsPage />} />
            <Route path="orgs" element={<AdminOrgsPage />} />
            <Route path="orgs/:orgId" element={<AdminOrgDetailPage />} />
            <Route path="websites" element={<AdminWebsitesPage />} />
            <Route
              path="websites/:websiteId"
              element={<AdminWebsiteDetailPage />}
            />
            <Route path="runs" element={<AdminRunsPage />} />
            <Route path="documents" element={<AdminDocumentsPage />} />
            <Route path="statements" element={<AdminStatementsPage />} />
            <Route path="recurring" element={<AdminRecurringPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="billing-events" element={<AdminBillingEventsPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="plans/:planId" element={<AdminPlanDetailPage />} />
            <Route path="pipeline" element={<AdminPipelinePage />} />
            <Route path="storage" element={<AdminStoragePage />} />
            <Route path="error-logs" element={<ErrorLogsPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
          </Route>
        </Route>

        {/* Fallback — 404 for unmatched routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieConsent />
    </ErrorBoundary>
  );
}
